import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  Icon,
  Screen,
  Select,
  Text,
  TextInput,
} from '~/atomic';
import { listInstitutionsRequest } from '~/services';
import { loadingService } from '~/services/loading';
import modalStatusService from '~/services/modalStatus';
import {
  indexerToApi,
  modalityToApi,
} from '~/store/contracts/mappers';
import type {
  ContractDraft,
  propsContractFrequency,
  propsContractIndexer,
  propsContractModality,
} from '~/store/contracts/interfaces';
import {
  parseAmountToCents,
  parseDateBRToISO,
  parsePercentToRate,
} from '~/utils/contractLabels';
import { maskMoney } from '~/utils/masks';
import { useAppDispatch } from '~/store';
import { createContractRequest, handleApiError } from '~/services';
import { fetchContracts } from '~/store/contracts/thunks';

const MODALITY_OPTS: { label: string; value: propsContractModality }[] = [
  { label: 'Custeio', value: 'custeio' },
  { label: 'Investimento', value: 'investimento' },
  { label: 'Capital de Giro', value: 'capital_giro' },
  { label: 'CPRF', value: 'cprf' },
  { label: 'Comercialização', value: 'comercializacao' },
  { label: 'Outros', value: 'outros' },
];

const AMORTIZATION_OPTS: { label: string; value: 'price' | 'sac' }[] = [
  { label: 'Tabela Price (parcela fixa)', value: 'price' },
  { label: 'SAC (amortização constante)', value: 'sac' },
];

const FREQUENCY_OPTS: { label: string; value: propsContractFrequency }[] = [
  { label: 'Mensal', value: 'monthly' },
  { label: 'Semestral', value: 'semiannual' },
  { label: 'Anual', value: 'annual' },
  { label: 'Por safra', value: 'harvest' },
];

const INDEXER_OPTS: { label: string; value: propsContractIndexer }[] = [
  { label: 'Pré-fixado', value: 'pre-fixed' },
  { label: 'CDI', value: 'cdi' },
  { label: 'IPCA', value: 'ipca' },
  { label: 'SELIC', value: 'selic' },
];

interface FormValues {
  contractNumber: string;
  amount: string;
  interestRate: string;
  installments: string;
  startDate: string;
  endDate: string;
  guarantee: string;
  cropDescription: string;
}

interface LocationState {
  draft?: ContractDraft;
  mode?: 'ai' | 'manual';
}

const isoToBRMask = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
};

const rateToInput = (rate?: number): string => {
  if (rate == null || !Number.isFinite(rate)) return '';
  return (rate * 100).toFixed(2).replace('.', ',');
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text className="text-captionLG color-complement-01 mb-nano uppercase">
      {children}
    </Text>
  );
}

export default function ContractReviewView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const draft: ContractDraft = useMemo(() => state.draft ?? {}, [state.draft]);
  const mode: 'ai' | 'manual' = state.mode ?? 'manual';

  const [institutions, setInstitutions] = useState<
    { label: string; value: string }[]
  >([]);
  const [institutionId, setInstitutionId] = useState<string | undefined>(
    draft.institution?.id,
  );
  const [modality, setModality] = useState<propsContractModality | undefined>(
    draft.modality,
  );
  const [amortizationType, setAmortizationType] = useState<'price' | 'sac'>(
    draft.amortizationType ?? 'price',
  );
  const [indexer, setIndexer] = useState<propsContractIndexer | undefined>(
    draft.indexer,
  );
  const [frequency, setFrequency] = useState<propsContractFrequency | undefined>(
    draft.frequency,
  );
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      contractNumber: draft.contractNumber ?? '',
      amount:
        draft.amountFinanced != null
          ? maskMoney(String(draft.amountFinanced))
          : '',
      interestRate: rateToInput(draft.interestRate),
      installments:
        draft.installmentsTotal != null ? String(draft.installmentsTotal) : '',
      startDate: isoToBRMask(draft.startDate),
      endDate: isoToBRMask(draft.endDate),
      guarantee: draft.guarantee ?? '',
      cropDescription: draft.cropDescription ?? '',
    },
  });

  useEffect(() => {
    let active = true;
    listInstitutionsRequest()
      .then((res) => {
        if (!active) return;
        const items = res.data.items.map((i) => ({ label: i.name, value: i.id }));
        setInstitutions(items);
        if (!institutionId && draft.institution?.name) {
          const found = res.data.items.find(
            (i) => i.name.toLowerCase() === draft.institution?.name?.toLowerCase(),
          );
          if (found) setInstitutionId(found.id);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const summaryMessage = useMemo(() => {
    if (mode === 'ai') {
      const schedule = draft.installmentSchedule ?? [];
      const paidCount = schedule.filter((s) => s.paid).length;
      if (paidCount > 0) {
        return {
          title: 'Dados extraídos do contrato.',
          description: `Revise antes de salvar. O documento indica ${paidCount} de ${schedule.length} parcelas já pagas — elas serão registradas como quitadas.`,
        };
      }
      return {
        title: 'Dados extraídos do contrato.',
        description: 'Revise e edite o que estiver incorreto antes de salvar.',
      };
    }
    return {
      title: 'Confira os dados informados.',
      description: 'Ajuste o que precisar antes de salvar.',
    };
  }, [mode, draft]);

  const onSubmit = handleSubmit(async (values) => {
    const missing: string[] = [];
    if (!institutionId) missing.push('Instituição');
    if (!values.contractNumber.trim()) missing.push('Nº do contrato');
    if (!modality) missing.push('Modalidade');
    if (!values.amount.trim() || parseAmountToCents(values.amount) === 0)
      missing.push('Valor financiado');
    if (!values.interestRate.trim()) missing.push('Taxa de juros');
    if (!indexer) missing.push('Indexador');
    if (!frequency) missing.push('Frequência');
    if (!values.installments.trim() || Number(values.installments) === 0)
      missing.push('Nº de parcelas');
    if (!parseDateBRToISO(values.startDate)) missing.push('Data de início');
    if (!parseDateBRToISO(values.endDate)) missing.push('Data de fim');

    if (missing.length > 0) {
      modalStatusService.show({
        type: 'error',
        title: 'Campos obrigatórios',
        description: `Preencha antes de salvar: ${missing.join(', ')}.`,
        textFirstButton: 'Entendi',
        showFirstButton: true,
        onClickFirstButton: modalStatusService.hide,
        handleCloseModal: modalStatusService.hide,
      });
      return;
    }

    setLoading(true);
    loadingService.show({
      title: 'Salvando contrato',
      description: 'Estamos criando o cronograma...',
    });

    try {
      await createContractRequest({
        institutionId: institutionId!,
        contractNumber: values.contractNumber.trim(),
        modality: modalityToApi(modality!),
        amortizationType,
        cedulaType: draft.cedulaType,
        amountFinancedCents: parseAmountToCents(values.amount),
        interestRate: parsePercentToRate(values.interestRate),
        indexer: indexerToApi[indexer!],
        frequency: frequency!,
        installmentsTotal: Number(values.installments),
        startDate: (parseDateBRToISO(values.startDate) ?? '').slice(0, 10),
        endDate: (parseDateBRToISO(values.endDate) ?? '').slice(0, 10),
        guarantee: values.guarantee.trim() || undefined,
        cropDescription: values.cropDescription.trim() || undefined,
        source: mode === 'ai' ? 'pdf_extraction' : 'manual',
        installmentSchedule: draft.installmentSchedule?.map((s) => ({
          dueDate: s.dueDate,
          amountCents: s.amountCents,
          paid: s.paid,
          paidDate: s.paidDate,
        })),
      });
      loadingService.hide();
      setLoading(false);
      await dispatch(fetchContracts());
      modalStatusService.show({
        type: 'success',
        title: 'Contrato salvo!',
        description: 'Seu contrato foi criado com sucesso.',
        textFirstButton: 'Ver no início',
        showFirstButton: true,
        onClickFirstButton: () => {
          modalStatusService.hide();
          navigate('/app/home', { replace: true });
        },
        handleCloseModal: () => {
          modalStatusService.hide();
          navigate('/app/home', { replace: true });
        },
      });
    } catch (error) {
      loadingService.hide();
      setLoading(false);
      handleApiError(error, {
        title: 'Não foi possível salvar',
        fallbackDescription: 'Verifique os campos e tente novamente.',
      });
    }
  });

  return (
    <Screen showHeader headerTitle="" hasPadding={false}>
      <form onSubmit={onSubmit} className="px-small pb-medium pt-medium">
        <Text className="text-headingH1 color-complement-01" as="h1">
          Revisar dados
        </Text>
        <Text className="mt-quark text-bodySM color-intense-06">
          Confirme e edite antes de salvar
        </Text>

        <div className="mt-medium flex items-start gap-smaller rounded-small bg-success-01 p-small">
          <Icon name="icon-ai" size={18} color="#1D4733" rounded={false} />
          <div className="flex-1">
            <Text className="text-bodyLG color-complement-01">{summaryMessage.title}</Text>
            <Text className="text-bodySM color-intense-06">
              {summaryMessage.description}
            </Text>
          </div>
        </div>

        <Box className="mt-medium">
          <FieldLabel>Instituição financeira</FieldLabel>
          <Select
            placeholder="Selecione a instituição"
            options={institutions}
            value={institutionId}
            onChange={(v) => setInstitutionId(v as string)}
          />
        </Box>

        <Box className="mt-smaller">
          <FieldLabel>Nº do contrato</FieldLabel>
          <TextInput control={control} name="contractNumber" placeholder="0000-0" />
        </Box>

        <Box className="mt-smaller flex flex-col md:flex-row gap-smaller">
          <Box className="flex-1">
            <FieldLabel>Modalidade do crédito</FieldLabel>
            <Select
              placeholder="Custeio"
              options={MODALITY_OPTS}
              value={modality}
              onChange={setModality}
            />
          </Box>
          <Box className="flex-1">
            <FieldLabel>Amortização</FieldLabel>
            <Select
              placeholder="Price"
              options={AMORTIZATION_OPTS}
              value={amortizationType}
              onChange={setAmortizationType}
            />
          </Box>
        </Box>

        <Box className="mt-smaller">
          <FieldLabel>Valor financiado</FieldLabel>
          <TextInput
            control={control}
            name="amount"
            placeholder="R$ 0,00"
            keyboardType="numeric"
            mask="money"
          />
          <Text className="mt-quark text-captionMD color-intense-05">
            Valor liberado, sem juros. O total a pagar é calculado pela parcela.
          </Text>
        </Box>

        <Box className="mt-smaller flex flex-col md:flex-row gap-smaller">
          <Box className="flex-1">
            <FieldLabel>Taxa de juros</FieldLabel>
            <TextInput
              control={control}
              name="interestRate"
              placeholder="% a.a."
              inputMode="decimal"
              mask="percent"
            />
          </Box>
          <Box className="flex-1">
            <FieldLabel>Indexador</FieldLabel>
            <Select
              placeholder="Pré-fixado"
              options={INDEXER_OPTS}
              value={indexer}
              onChange={setIndexer}
            />
          </Box>
        </Box>

        <Box className="mt-smaller flex flex-col md:flex-row gap-smaller">
          <Box className="flex-1">
            <FieldLabel>Frequência</FieldLabel>
            <Select
              placeholder="Mensal"
              options={FREQUENCY_OPTS}
              value={frequency}
              onChange={setFrequency}
            />
          </Box>
          <Box className="flex-1">
            <FieldLabel>Nº de parcelas</FieldLabel>
            <TextInput
              control={control}
              name="installments"
              placeholder="0"
              keyboardType="numeric"
              mask="integer"
            />
          </Box>
        </Box>

        <Box className="mt-smaller flex flex-col md:flex-row gap-smaller">
          <Box className="flex-1">
            <FieldLabel>Início</FieldLabel>
            <TextInput
              control={control}
              name="startDate"
              placeholder="dd/mm/aaaa"
              keyboardType="numeric"
              mask="date"
            />
          </Box>
          <Box className="flex-1">
            <FieldLabel>Encerramento</FieldLabel>
            <TextInput
              control={control}
              name="endDate"
              placeholder="dd/mm/aaaa"
              keyboardType="numeric"
              mask="date"
            />
          </Box>
        </Box>

        <Box className="mt-smaller">
          <FieldLabel>Garantia (opcional)</FieldLabel>
          <TextInput
            control={control}
            name="guarantee"
            placeholder="Penhor agrícola, hipoteca rural..."
          />
        </Box>

        <Box className="mt-smaller">
          <FieldLabel>Descrição / cultura (opcional)</FieldLabel>
          <TextInput
            control={control}
            name="cropDescription"
            placeholder="Custeio Soja safra 2025/2026"
          />
        </Box>

        <Box className="mt-medium">
          <Button
            type="submit"
            text="Salvar contrato"
            rightIcon="icon-check"
            loading={loading}
            fullWidth
          />
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-smaller w-full text-center mb-huge"
          >
            <Text className="text-bodyMD color-intense-06">Cancelar</Text>
          </button>
        </Box>
      </form>
    </Screen>
  );
}
